import { useState } from "react";
import {MapPin, Phone, Mail, Clock, MessageCircleDashed as MessageCircle, CheckCircle, ArrowRight, } from 'lucide-react';

const contactInfo = [
  {
    icon: MapPin,
    title: "Endereço",
    lines: ["CPO São Cristóvão, 388", "São Cristóvão – Rio de Janeiro", "CEP: 20.921-440"],
  },
  {
    icon: Phone,
    title: "Telefones",
    lines: ["(21) 97351-6085", "WhatsApp: (21) 97351-6085"],
  },
  {
    icon: Mail,
    title: "E-mail",
    lines: ["suporte@manguenela.com.br", "administrativo@manguenela.com.br"],
  },
  {
    icon: Clock,
    title: "Horário de Atendimento",
    lines: ["Seg a Sex: 08h00 às 17h45", "Sábado: 08h00 às 13h00", "Atendimento especializado via WhatsApp"],
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const msg = `Olá! Sou ${form.name}.\n\nAssunto: ${form.subject}\nEmail: ${form.email}\nTelefone: ${form.phone}\n\n${form.message}`;
    window.open(`https://wa.me/5521973516085?text=${encodeURIComponent(msg)}`, "_blank");
    setSent(true);
    setTimeout(() => setSent(false), 5000);
    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
  };

  const inputCls =
    "w-full border border-gray-200 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/15 transition-all duration-200";

  return (
    <section id="contato" className="bg-white py-24 md:py-32 relative overflow-hidden">

      {/* Subtle top divider accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#F5A623] to-transparent opacity-40" />

      {/* Ambient glows */}
      <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-[#F5A623]/6 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[22rem] h-[22rem] bg-[#0D1B3E]/4 rounded-full blur-[80px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">

        {/* ── Header ── */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <div>
            <span className="inline-flex items-center gap-3 text-[#F5A623] font-black uppercase tracking-[0.25em] text-xs mb-4">
              <span className="w-10 h-px bg-[#F5A623]" />
              Entre em Contato
            </span>
            <h2 className="font-barlow-condensed text-5xl md:text-7xl font-black text-[#0D1B3E] uppercase leading-[0.9]">
              Fale com a<br />
              <span className="text-[#F5A623]">Manguenela Rio</span>
            </h2>
            <p className="mt-4 text-gray-500 text-lg max-w-lg leading-relaxed font-light">
              Envie sua mensagem ou solicite um orçamento. Nossa equipe responde rapidamente!
            </p>
          </div>
          <div className="h-px flex-1 max-w-xs bg-gradient-to-r from-[#F5A623]/60 to-transparent hidden md:block mb-4" />
        </div>

        <div className="grid lg:grid-cols-5 gap-10">

          {/* ── Info cards ── */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {contactInfo.map(({ icon: Icon, title, lines }) => (
              <div
                key={title}
                className="card-lift bg-white border border-gray-100 rounded-2xl p-5 flex gap-4 hover:border-[#F5A623]/40 hover:shadow-[0_16px_32px_-8px_rgba(13,27,62,0.15)]"
              >
                <div className="w-11 h-11 bg-gradient-to-br from-[#F5A623] to-[#d4890e] rounded-xl flex items-center justify-center shrink-0 shadow-md shadow-[#F5A623]/20">
                  <Icon size={18} className="text-[#0D1B3E]" />
                </div>
                <div>
                  <p className="text-[#0D1B3E] font-black text-xs uppercase tracking-[0.18em] mb-1.5">{title}</p>
                  {lines.map((line) => (
                    <p key={line} className="text-gray-500 text-sm leading-relaxed">{line}</p>
                  ))}
                </div>
              </div>
            ))}

            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/5521973516085?text=Olá! Gostaria de mais informações."
              target="_blank"
              rel="noopener noreferrer"
              className="card-lift flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20ba5a] text-white font-black py-4 rounded-2xl uppercase tracking-widest text-sm shadow-lg shadow-[#25D366]/20 hover:shadow-[#25D366]/35 transition-all duration-200"
            >
              <MessageCircle size={19} />
              Chamar no WhatsApp
            </a>
          </div>

          {/* ── Form ── */}
          <div className="lg:col-span-3 bg-white rounded-2xl p-8 md:p-10 shadow-[0_24px_64px_-16px_rgba(13,27,62,0.18)] border border-gray-100">
            {sent ? (
              <div className="flex flex-col items-center justify-center min-h-72 gap-5">
                <div className="w-20 h-20 bg-[#25D366]/10 rounded-full flex items-center justify-center">
                  <CheckCircle size={44} className="text-[#25D366]" />
                </div>
                <h3 className="text-[#0D1B3E] font-black text-2xl font-barlow-condensed uppercase">
                  Mensagem Enviada!
                </h3>
                <p className="text-gray-500 text-center text-sm max-w-xs leading-relaxed">
                  Você foi redirecionado para o WhatsApp. Nossa equipe responderá em breve!
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="mb-2">
                  <h3 className="text-[#0D1B3E] font-black text-2xl font-barlow-condensed uppercase">
                    Solicite um Orçamento
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">Preencha o formulário e enviaremos pelo WhatsApp.</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#0D1B3E] text-xs font-black uppercase tracking-wider mb-2">Nome *</label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="Seu nome completo" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-[#0D1B3E] text-xs font-black uppercase tracking-wider mb-2">Telefone *</label>
                    <input type="tel" name="phone" value={form.phone} onChange={handleChange} required placeholder="(21) 9 0000-0000" className={inputCls} />
                  </div>
                </div>

                <div>
                  <label className="block text-[#0D1B3E] text-xs font-black uppercase tracking-wider mb-2">E-mail</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="seu@email.com" className={inputCls} />
                </div>

                <div>
                  <label className="block text-[#0D1B3E] text-xs font-black uppercase tracking-wider mb-2">Assunto *</label>
                  <select name="subject" value={form.subject} onChange={handleChange} required className={inputCls}>
                    <option value="">Selecione o assunto</option>
                    <option value="Orçamento de Mangueiras">Orçamento de Mangueiras</option>
                    <option value="Orçamento de Conexões">Orçamento de Conexões</option>
                    <option value="Orçamento de Correias">Orçamento de Correias</option>
                    <option value="Assistência Técnica">Assistência Técnica</option>
                    <option value="Informações Gerais">Informações Gerais</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[#0D1B3E] text-xs font-black uppercase tracking-wider mb-2">Mensagem *</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="Descreva sua necessidade, produto, quantidade e aplicação..."
                    className={`${inputCls} resize-none`}
                  />
                </div>

                <button
                  type="submit"
                  className="group flex items-center justify-center gap-2 bg-[#F5A623] hover:bg-[#ffc048] text-[#0D1B3E] font-black py-4 rounded-xl uppercase tracking-widest text-sm transition-all duration-200 hover:-translate-y-0.5 shadow-xl shadow-[#F5A623]/25 hover:shadow-[#F5A623]/40"
                >
                  Enviar via WhatsApp
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>

                <p className="text-gray-300 text-xs text-center">
                  Ao enviar, você será redirecionado ao WhatsApp com sua mensagem pronta.
                </p>
              </form>
            )}
          </div>
        </div>

        {/* ── Map ── */}
        <div className="mt-14 rounded-2xl overflow-hidden border border-gray-100 h-72 shadow-[0_16px_40px_-12px_rgba(13,27,62,0.15)]">
          <iframe
            title="Localização Manguenela Rio"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3673.3!2d-43.222!3d-22.890!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sCPO+S%C3%A3o+Crist%C3%B3v%C3%A3o+388+Rio+de+Janeiro!5e0!3m2!1spt-BR!2sbr!4v1"
          />
        </div>
      </div>
    </section>
  );
}
